import { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { heroSlides } from '../data/media.js';
import './Hero.css';

const INTERVAL_MS = 6500;

/** True when the OS asks for reduced motion. Re-evaluates if the user changes it. */
function usePrefersReducedMotion() {
  const [reduced, setReduced] = useState(
    () => window.matchMedia?.('(prefers-reduced-motion: reduce)').matches ?? false
  );

  useEffect(() => {
    const mq = window.matchMedia('(prefers-reduced-motion: reduce)');
    const onChange = (e) => setReduced(e.matches);
    mq.addEventListener('change', onChange);
    return () => mq.removeEventListener('change', onChange);
  }, []);

  return reduced;
}

export default function Hero() {
  const reducedMotion = usePrefersReducedMotion();
  const [index, setIndex] = useState(0);
  // Auto-rotation is off from the start when reduced motion is requested —
  // a carousel that moves on its own is exactly what that preference is about.
  const [playing, setPlaying] = useState(!reducedMotion);

  useEffect(() => {
    if (reducedMotion) setPlaying(false);
  }, [reducedMotion]);

  const goTo = useCallback((i) => setIndex(((i % heroSlides.length) + heroSlides.length) % heroSlides.length), []);

  useEffect(() => {
    if (!playing) return undefined;
    const id = window.setInterval(() => setIndex((i) => (i + 1) % heroSlides.length), INTERVAL_MS);
    return () => window.clearInterval(id);
  }, [playing]);

  // Pause while the tab is hidden so we aren't cycling images nobody can see.
  useEffect(() => {
    const onVisibility = () => {
      if (document.hidden) setPlaying(false);
    };
    document.addEventListener('visibilitychange', onVisibility);
    return () => document.removeEventListener('visibilitychange', onVisibility);
  }, []);

  return (
    <section
      className="hero on-dark"
      aria-label="Welcome"
      aria-roledescription="carousel"
      onMouseEnter={() => setPlaying(false)}
      onMouseLeave={() => !reducedMotion && setPlaying(true)}
      onFocusCapture={() => setPlaying(false)}
    >
      <div className="hero__slides">
        {heroSlides.map((slide, i) => (
          <div
            key={slide.src}
            className={`hero__slide${i === index ? ' hero__slide--active' : ''}`}
            aria-hidden={i !== index}
          >
            <img
              src={slide.src}
              alt={slide.alt}
              // The first slide is the LCP element; everything after it waits.
              loading={i === 0 ? 'eager' : 'lazy'}
              fetchPriority={i === 0 ? 'high' : 'low'}
              decoding="async"
            />
          </div>
        ))}
      </div>

      <div className="hero__scrim" aria-hidden="true" />

      <div className="container hero__inner">
        <div className="hero__panel">
          <p className="section-eyebrow">Welcome home</p>

          <h1 className="hero__title">
            Welcome to
            <span>Metropolitan</span>
          </h1>

          <p className="hero__lede">
            A community of believers in northwest Detroit, committed to God, to
            fellowship, and to service. Whether it is your first Sunday or your
            fiftieth year, there is a place for you here.
          </p>

          <div className="hero__actions">
            <Link to="/visiting/location-service-times" className="btn btn--gold">
              Plan Your Visit
            </Link>
            <Link to="/watch/live" className="btn btn--secondary">
              Watch Online
            </Link>
          </div>

          <div className="hero__controls">
            {heroSlides.map((slide, i) => (
              <button
                key={slide.src}
                type="button"
                className="hero__dot"
                aria-current={i === index}
                aria-label={`Show image ${i + 1} of ${heroSlides.length}`}
                onClick={() => goTo(i)}
              />
            ))}

            <button
              type="button"
              className="hero__pause"
              onClick={() => setPlaying((p) => !p)}
              aria-label={playing ? 'Pause image rotation' : 'Play image rotation'}
            >
              {playing ? (
                <svg width="12" height="14" viewBox="0 0 12 14" fill="currentColor" aria-hidden="true">
                  <rect x="0" y="0" width="4" height="14" rx="1" />
                  <rect x="8" y="0" width="4" height="14" rx="1" />
                </svg>
              ) : (
                <svg width="12" height="14" viewBox="0 0 12 14" fill="currentColor" aria-hidden="true">
                  <path d="M0 0l12 7-12 7V0z" />
                </svg>
              )}
            </button>
          </div>
        </div>
      </div>

      {/* Announces slide changes without moving focus. */}
      <p className="sr-only" aria-live="polite">
        Image {index + 1} of {heroSlides.length}: {heroSlides[index].alt}
      </p>
    </section>
  );
}
